import type { NewsItem } from '../types/news'

export interface SavedStory {
  item: NewsItem
  savedAt: string
}

export interface ReaderHabit {
  streak: number
  activeDaysThisWeek: number
  totalActiveDays: number
  visitedDays: string[]
}

export interface ReadingCheckpoint {
  articleId: string
  title: string
  href: string
  progress: number
  scrollY?: number
  updatedAt: string
}

export interface NewsViewPreferences {
  coverageMode?: string
  category?: string
  industry?: string
  sourceRegion?: string
}

export type ReaderEdition = 'robots' | 'cybersecurity'
type EditionTopicPreferences = Partial<Record<ReaderEdition, string[]>>

const SAVED_KEY = 'omikina:saved-stories'
const TOPICS_KEY = 'omikina:preferred-topics'
const LAST_VISIT_KEY = 'omikina:last-visit'
const SESSION_PREVIOUS_VISIT_KEY = 'omikina:session-previous-visit'
const CHECKLIST_KEY = 'omikina:due-diligence-checklist'
const HABIT_KEY = 'omikina:reader-habit'
const OPENED_STORIES_KEY = 'omikina:opened-stories'
const READING_CHECKPOINT_KEY = 'omikina:reading-checkpoint'
const NEWS_VIEW_KEY = 'omikina:news-view'
const EDITION_TOPICS_KEY = 'omikina:edition-topics'
const LEGACY_EDITION_TOPIC_KEYS: Record<ReaderEdition, string> = {
  robots: 'omikina:robot-news-preferences:v1',
  cybersecurity: 'omikina:cybersecurity-preferences:v1',
}
export const READER_CHANGED_EVENT = 'omikina:reader-changed'

function readJson<T>(key: string, fallback: T): T {
  try { return JSON.parse(window.localStorage.getItem(key) ?? '') as T } catch { return fallback }
}

export function getSavedStories(): SavedStory[] {
  const stories = readJson<SavedStory[]>(SAVED_KEY, [])
  return Array.isArray(stories) ? stories.filter((story) => story?.item?.id).slice(0, 100) : []
}

export function isStorySaved(id: string): boolean {
  return getSavedStories().some((story) => story.item.id === id)
}

export function toggleSavedStory(item: NewsItem): boolean {
  const stories = getSavedStories()
  const exists = stories.some((story) => story.item.id === item.id)
  const next = exists ? stories.filter((story) => story.item.id !== item.id) : [{ item, savedAt: new Date().toISOString() }, ...stories]
  window.localStorage.setItem(SAVED_KEY, JSON.stringify(next))
  window.dispatchEvent(new CustomEvent(READER_CHANGED_EVENT))
  return !exists
}

export function getPreferredTopics(): string[] {
  const topics = readJson<string[]>(TOPICS_KEY, [])
  return Array.isArray(topics) ? topics.filter((topic) => typeof topic === 'string') : []
}

export function togglePreferredTopic(slug: string): boolean {
  const topics = getPreferredTopics()
  const exists = topics.includes(slug)
  const next = exists ? topics.filter((topic) => topic !== slug) : [...topics, slug]
  window.localStorage.setItem(TOPICS_KEY, JSON.stringify(next))
  window.dispatchEvent(new CustomEvent(READER_CHANGED_EVENT))
  return !exists
}

export function getEditionTopics(edition: ReaderEdition): string[] {
  const stored = readJson<EditionTopicPreferences>(EDITION_TOPICS_KEY, {})[edition]
  if (Array.isArray(stored)) return stored.filter((topic) => typeof topic === 'string')
  const legacy = readJson<string[]>(LEGACY_EDITION_TOPIC_KEYS[edition], [])
  return Array.isArray(legacy) ? legacy.filter((topic) => typeof topic === 'string') : []
}

export function setEditionTopics(edition: ReaderEdition, topics: string[]) {
  const current = readJson<EditionTopicPreferences>(EDITION_TOPICS_KEY, {})
  const next = { ...current, [edition]: Array.from(new Set(topics.filter((topic) => typeof topic === 'string'))) }
  window.localStorage.setItem(EDITION_TOPICS_KEY, JSON.stringify(next))
  window.localStorage.removeItem(LEGACY_EDITION_TOPIC_KEYS[edition])
  window.dispatchEvent(new CustomEvent(READER_CHANGED_EVENT))
}

export function toggleEditionTopic(edition: ReaderEdition, topic: string): boolean {
  const topics = getEditionTopics(edition)
  const exists = topics.includes(topic)
  setEditionTopics(edition, exists ? topics.filter((value) => value !== topic) : [...topics, topic])
  return !exists
}

export function clearReaderData() {
  window.localStorage.removeItem(SAVED_KEY)
  window.localStorage.removeItem(TOPICS_KEY)
  window.localStorage.removeItem(LAST_VISIT_KEY)
  window.sessionStorage.removeItem(SESSION_PREVIOUS_VISIT_KEY)
  window.localStorage.removeItem(CHECKLIST_KEY)
  window.localStorage.removeItem(HABIT_KEY)
  window.localStorage.removeItem(OPENED_STORIES_KEY)
  window.localStorage.removeItem(READING_CHECKPOINT_KEY)
  window.localStorage.removeItem(NEWS_VIEW_KEY)
  window.localStorage.removeItem(EDITION_TOPICS_KEY)
  Object.values(LEGACY_EDITION_TOPIC_KEYS).forEach((key) => window.localStorage.removeItem(key))
  window.dispatchEvent(new CustomEvent(READER_CHANGED_EVENT))
}

export function getChecklistProgress(): string[] {
  const ids = readJson<string[]>(CHECKLIST_KEY, [])
  return Array.isArray(ids) ? ids.filter((id) => typeof id === 'string') : []
}

export function setChecklistItem(id: string, checked: boolean): string[] {
  const current = new Set(getChecklistProgress())
  if (checked) current.add(id)
  else current.delete(id)
  const next = Array.from(current)
  window.localStorage.setItem(CHECKLIST_KEY, JSON.stringify(next))
  return next
}

export function clearChecklistProgress() {
  window.localStorage.removeItem(CHECKLIST_KEY)
}

export function getPreviousVisit(): string | null {
  recordReaderVisit()
  const sessionValue = window.sessionStorage.getItem(SESSION_PREVIOUS_VISIT_KEY)
  if (sessionValue !== null) return sessionValue || null
  const previous = window.localStorage.getItem(LAST_VISIT_KEY)
  window.sessionStorage.setItem(SESSION_PREVIOUS_VISIT_KEY, previous ?? '')
  window.localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString())
  return previous
}

function localDay(date = new Date()): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function dayOffset(date: Date, amount: number): Date {
  const next = new Date(date)
  next.setHours(12, 0, 0, 0)
  next.setDate(next.getDate() + amount)
  return next
}

export function recordReaderVisit(): ReaderHabit {
  const current = readJson<{ visitedDays?: string[] }>(HABIT_KEY, {})
  const today = localDay()
  const visitedDays = Array.from(new Set([...(current.visitedDays ?? []).filter((day) => /^\d{4}-\d{2}-\d{2}$/.test(day)), today])).sort().slice(-90)
  window.localStorage.setItem(HABIT_KEY, JSON.stringify({ visitedDays }))
  return readerHabitFromDays(visitedDays)
}

function readerHabitFromDays(visitedDays: string[]): ReaderHabit {
  const days = new Set(visitedDays)
  const now = new Date()
  const start = days.has(localDay(now)) ? now : dayOffset(now, -1)
  let streak = 0
  for (let offset = 0; days.has(localDay(dayOffset(start, -offset))); offset += 1) streak += 1
  const activeDaysThisWeek = Array.from({ length: 7 }, (_, index) => localDay(dayOffset(now, -index))).filter((day) => days.has(day)).length
  return { streak, activeDaysThisWeek, totalActiveDays: visitedDays.length, visitedDays }
}

export function getReaderHabit(): ReaderHabit {
  const current = readJson<{ visitedDays?: string[] }>(HABIT_KEY, {})
  return readerHabitFromDays((current.visitedDays ?? []).filter((day) => typeof day === 'string'))
}

type OpenedStory = { id: string; openedAt: string }

export function getOpenedStoryIds(): string[] {
  const opened = readJson<OpenedStory[]>(OPENED_STORIES_KEY, [])
  return Array.isArray(opened) ? opened.filter((item) => item?.id).map((item) => item.id) : []
}

export function markStoryOpened(id: string) {
  const opened = readJson<OpenedStory[]>(OPENED_STORIES_KEY, []).filter((item) => item?.id !== id)
  window.localStorage.setItem(OPENED_STORIES_KEY, JSON.stringify([{ id, openedAt: new Date().toISOString() }, ...opened].slice(0, 200)))
  window.dispatchEvent(new CustomEvent(READER_CHANGED_EVENT))
}

export function getReadingCheckpoint(): ReadingCheckpoint | null {
  const checkpoint = readJson<ReadingCheckpoint | null>(READING_CHECKPOINT_KEY, null)
  return checkpoint?.articleId && checkpoint.href && checkpoint.title ? checkpoint : null
}

export function setReadingCheckpoint(checkpoint: Omit<ReadingCheckpoint, 'updatedAt'>) {
  window.localStorage.setItem(READING_CHECKPOINT_KEY, JSON.stringify({ ...checkpoint, progress: Math.max(0, Math.min(100, Math.round(checkpoint.progress))), scrollY: checkpoint.scrollY === undefined ? undefined : Math.max(0, Math.round(checkpoint.scrollY)), updatedAt: new Date().toISOString() }))
  window.dispatchEvent(new CustomEvent(READER_CHANGED_EVENT))
}

export function getNewsViewPreferences(): NewsViewPreferences {
  return readJson<NewsViewPreferences>(NEWS_VIEW_KEY, {})
}

export function setNewsViewPreferences(preferences: NewsViewPreferences) {
  window.localStorage.setItem(NEWS_VIEW_KEY, JSON.stringify(preferences))
}

export function countItemsSince(items: NewsItem[], iso: string | null): number {
  if (!iso) return 0
  const boundary = new Date(iso).getTime()
  if (!Number.isFinite(boundary)) return 0
  return items.filter((item) => new Date(item.publishedAt).getTime() > boundary).length
}
